import { OrderProductResponse } from '@business/entities/order';
import { ProposalDiscountType } from '@business/entities/proposal';
import { EstimateGroupResponse, SubProjectOrderEstimateResponse, TimeMaterialResponse, TMFeeResponse } from '@business/entities/tm';
import { ClientListItemResponse } from '@business/entities/tm/EstimatorTemplateListResponse';
import { R } from '@core/utils/r';
import { TmInvoiceSalesTaxData } from '@modules/common/TmInvoiceSalesTax';
import { ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import {
  EstimateGroupLineItemData,
  EstimateItemFormData,
  EstimateItemGroupData,
} from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';

export function getMaterialCost(item: EstimateGroupLineItemData) {
  const totalQty = R.toNumber(item.quantity) + R.toNumber(item?.manualQty ?? 0); // item?.manualQty ?? 0 this condition is for old proposals which doesnot have manualQty key
  return totalQty * item.materialPrice;
}

export function getMaterialPrice(item: EstimateGroupLineItemData) {
  return getMaterialCost(item) + (getMaterialCost(item) * item.markupFactor) / 100;
}

export function getVccGroupSubtotal(group: EstimateItemGroupData) {
  return group.items.reduce((acc, item) => acc + (!item.weProvide ? getMaterialPrice(item) : 0), 0);
}

export function getVccGroupMarkup(group: EstimateItemGroupData) {
  return getVccGroupSubtotal(group) * (group.markupFactor / 100);
}

export function getVccGroupTotal(group: EstimateItemGroupData) {
  return getVccGroupSubtotal(group) + getVccGroupMarkup(group);
}

export function getVccTotal(estimate: EstimateItemFormData) {
  return estimate.groups.reduce((acc, group) => acc + getVccGroupTotal(group), 0);
}

export function getSubTotal(item: EstimateGroupLineItemData) {
  const totalQty = R.toNumber(item.quantity) + R.toNumber(item?.manualQty ?? 0); // item?.manualQty ?? 0 this condition is for old proposals which doesnot have manualQty key
  if (item.weProvide) {
    const labor = totalQty * item.laborPrice;
    const material = totalQty * item.materialPrice;
    return labor + material;
  }
  return totalQty * item.laborPrice;
}

export function getItemMarkup(item: EstimateGroupLineItemData) {
  return getSubTotal(item) * (item.markupFactor / 100);
}

export function getItemTotal(item: EstimateGroupLineItemData) {
  return getSubTotal(item) + getItemMarkup(item);
}

export function getGroupSubtotal(group: EstimateItemGroupData) {
  return group.items.reduce((acc, item) => acc + getItemTotal(item), 0);
}

export function getGroupMarkup(group: EstimateItemGroupData) {
  return getGroupSubtotal(group) * (group.markupFactor / 100);
}

export function getGroupTotal(group: EstimateItemGroupData) {
  return getGroupSubtotal(group) + getGroupMarkup(group);
}

export function getEstimateFeeTotal(item: EstimateItemFormData) {
  // fee.adjustmentAmount ?? 0: proposals saved before fee adjustments existed have no adjustmentAmount key (the total
  // showed $NaN)
  return item.fees.filter(o => o.isChecked).reduce((acc, fee) => acc + fee.amount + (fee.adjustmentAmount ?? 0), 0);
}

export function getEstimateSubTotal(item: EstimateItemFormData) {
  return item.groups.reduce((acc, group) => acc + getGroupTotal(group), 0);
}

export function getEstimateTotal(item: EstimateItemFormData) {
  return getEstimateSubTotal(item) + getEstimateFeeTotal(item);
}

export function getEstimatedCost(item: EstimateItemFormData) {
  return getEstimateSubTotal(item) + getEstimateFeeTotal(item);
}

export function getSalePrice(product: OrderProductResponse) {
  if (product.weProvidedBy) return R.toNumber(product.internalLabourPerHourPrice) + R.toNumber(product.internalMaterialPerUnitPrice);
  else return R.toNumber(product.internalLabourPerHourPrice);
}

// export function getVccTotal(estimate: EstimateItemFormData) {
//   const vccGroups = estimate.groups.filter(group => group.items.filter(o => !o.weProvide).length > 0);
//   return getEstimateSubTotal({ ...estimate, groups: vccGroups });
// }

export function getSalesTax(salesTax: TmInvoiceSalesTaxData, amt: number) {
  if (salesTax && salesTax?.isSalesTax) {
    if (salesTax.isManual) {
      return salesTax.taxAmount;
    } else {
      return (amt * salesTax.value) / 100;
    }
  } else {
    return 0;
  }
}

export function getDiscount(total: number, discount: ProposalDiscountFormData) {
  if (discount.isDiscount) {
    if (discount.type === ProposalDiscountType.percentage) {
      return total * (discount.value / 100);
    } else {
      return discount.value;
    }
  } else {
    return 0;
  }
}

/**
 * A Work Order's total discount: its discount history (proposal + change orders) when it has one, otherwise the
 * estimate's discount (Work Orders whose history wasn't backfilled yet).
 */
export function getWorkOrderDiscount(order: TimeMaterialResponse) {
  if (order.discounts?.length && order.billingSummary) {
    return order.billingSummary.totalDiscounts;
  }
  return getDiscount(order.subtotal, order.estimate.discount as ProposalDiscountFormData);
}

// for SummaryPage

export function getSubProjectProposedAmount(projects: SubProjectOrderEstimateResponse[]) {
  return projects.reduce((acc, item) => acc + item.total, 0);
}

export function getItemProfitLossSubTotalAmount(item: EstimateGroupResponse) {
  return item.total + item.adjustmentAmt + item.coTotal - item.invoiceTotal;
}

export function getSubTotalProposedAmount(items: EstimateGroupResponse[]) {
  return items.reduce((acc, item) => acc + item.total, 0);
}

export function getSubTotalAdjustmentAmount(items: EstimateGroupResponse[]) {
  return items.reduce((acc, item) => acc + item.adjustmentAmt, 0);
}

export function getItemSubTotalAmount(items: EstimateGroupResponse[]) {
  return getSubTotalProposedAmount(items) + getSubTotalAdjustmentAmount(items);
}

export function getSubTotalProposedFeeAmount(fees: TMFeeResponse[]) {
  return fees.reduce((acc, item) => acc + item.amount, 0);
}

export function getSubTotalAdjustmentFeeAmount(fees: TMFeeResponse[]) {
  return fees.reduce((acc, item) => acc + item.adjustmentAmount, 0);
}

export function getFeeSubTotalAmount(fees: TMFeeResponse[]) {
  return getSubTotalProposedFeeAmount(fees) + getSubTotalAdjustmentFeeAmount(fees);
}

export function getCoSubTotalAmount(items: EstimateGroupResponse[]) {
  return items.reduce((acc, item) => acc + item.coTotal, 0);
}

export function getProviderInvoiceSubTotalAmount(items: EstimateGroupResponse[]) {
  return items.reduce((acc, item) => acc + item.invoiceTotal, 0);
}

export function getProfitLossSubTotalAmount(items: EstimateGroupResponse[]) {
  return getSubTotalProposedAmount(items) + getSubTotalAdjustmentAmount(items) + getCoSubTotalAmount(items) - getProviderInvoiceSubTotalAmount(items);
}

// Single source of truth for list item money math on the PROPOSAL side. Mirrors the backend
// (TmListItem / EstimateItem): billed amount = qty × (labor + material-if-weProvide) × markup;
// VCC = material of NOT-we-provide items (× markup), shown separately and never billed.
// `quantity` must already include manualQty.
export function getListItemAmounts(item: { quantity: number; laborPrice: number; materialPrice: number; weProvide: boolean; markupFactor?: number }) {
  const markup = 1 + (item.markupFactor ?? 0) / 100;
  const fixed = item.quantity * (item.laborPrice + (item.weProvide ? item.materialPrice : 0)) * markup;
  const vcc = item.weProvide ? 0 : item.quantity * item.materialPrice * markup;
  return { fixed, vcc, total: fixed };
}

export function getListItemsProposedAmount(listItems: ClientListItemResponse[]) {
  return listItems.reduce((acc, item) => acc + (item.initialApprove ? item.total : 0), 0);
}

export function getContractProposedAmount(
  items: EstimateGroupResponse[],
  fees: TMFeeResponse[],
  projects?: SubProjectOrderEstimateResponse[],
  listItems?: ClientListItemResponse[],
) {
  return (
    getSubTotalProposedAmount(items) +
    getSubTotalProposedFeeAmount(fees) +
    getSubProjectProposedAmount(projects ?? []) +
    getListItemsProposedAmount(listItems ?? [])
  );
}

export function getContractAdjustmentAmount(items: EstimateGroupResponse[], fees: TMFeeResponse[]) {
  return getSubTotalAdjustmentAmount(items) + getSubTotalAdjustmentFeeAmount(fees);
}

export function getContractSubTotalAmount(
  items: EstimateGroupResponse[],
  fees: TMFeeResponse[],
  projects?: SubProjectOrderEstimateResponse[],
  listItems?: ClientListItemResponse[],
) {
  return getContractProposedAmount(items, fees, projects ?? [], listItems ?? []) + getContractAdjustmentAmount(items, fees);
}

// end for SummaryPage
